import type { AABB } from '../pc/colliders'
import { MAX_STEP, moveAndResolveCollisions } from '../pc/colliders'
import {
  CARRIAGEWAY_MIN_Z,
  CARRIAGEWAY_MAX_Z,
  PLOTS,
  plotDoorPosition,
} from '../pc/world/district'
import type { AssetZoneSnapshot } from '../pc/assetZones'

export type CampusAcceptanceViewId =
  | 'spawn'
  | 'west'
  | 'east'
  | 'board'
  | 'ops'
  | 'hq'
  | 'exchange-entry'
  | 'exchange-silhouette'
  | 'workshop-entry'
  | 'workshop-silhouette'
  | 'library'
  | 'cafe'
  | 'datacentre-entry'
  | 'datacentre-silhouette'
  | 'north-east-context'
  | 'south-east-context'

export interface CampusAcceptancePose {
  x: number
  y: number
  z: number
  yawDeg: number
  pitchDeg: number
}

export type CampusDestinationViewId = Exclude<
  CampusAcceptanceViewId,
  'spawn' | 'west' | 'east'
>

export const CAMPUS_DESTINATION_VIEW_IDS: readonly CampusDestinationViewId[] = [
  'board',
  'ops',
  'hq',
  'exchange-entry',
  'exchange-silhouette',
  'workshop-entry',
  'workshop-silhouette',
  'cafe',
  'library',
  'datacentre-entry',
  'datacentre-silhouette',
  'north-east-context',
  'south-east-context',
] as const

export interface CampusDestinationEvidenceSpec {
  plotId: string
  destination: string
  entityName: string
}

/** Scene entities whose rendered geometry must be visible in each capture. */
export const CAMPUS_DESTINATION_EVIDENCE: Readonly<
  Record<CampusDestinationViewId, CampusDestinationEvidenceSpec>
> = {
  board: { plotId: 'P1', destination: 'BOARD', entityName: 'plot-P1-building' },
  ops: { plotId: 'P2', destination: 'OPS', entityName: 'plot-P2-building' },
  hq: { plotId: 'P3', destination: 'ANIMUS HQ', entityName: 'plot-P3-building' },
  'exchange-entry': { plotId: 'P4', destination: 'EXCHANGE', entityName: 'plot-P4-building' },
  'exchange-silhouette': { plotId: 'P4', destination: 'EXCHANGE', entityName: 'plot-P4-building' },
  'workshop-entry': { plotId: 'P5', destination: 'WORKSHOP', entityName: 'plot-P5-building' },
  'workshop-silhouette': { plotId: 'P5', destination: 'WORKSHOP', entityName: 'plot-P5-building' },
  cafe: { plotId: 'P6', destination: 'CAFE', entityName: 'plot-P6-building' },
  library: { plotId: 'P7', destination: 'LIBRARY', entityName: 'plot-P7-building' },
  'datacentre-entry': { plotId: 'P8', destination: 'DATACENTRE', entityName: 'plot-P8-building' },
  'datacentre-silhouette': { plotId: 'P8', destination: 'DATACENTRE', entityName: 'plot-P8-building' },
  'north-east-context': { plotId: 'P4', destination: 'EAST CAMPUS', entityName: 'plot-P4-building' },
  'south-east-context': { plotId: 'P8', destination: 'EAST CAMPUS', entityName: 'plot-P8-building' },
}

export interface CampusRenderEvidence {
  entityName: string
  renderComponents: number
  meshInstances: number
  visibleMeshInstances: number
  worldBounds: {
    minX: number
    maxX: number
    minY: number
    maxY: number
    minZ: number
    maxZ: number
  } | null
}

export interface CampusCameraEvidence {
  x: number
  y: number
  z: number
  yawDeg: number
  pitchDeg: number
}

export interface CampusViewDiagnostics {
  schema: 'arena.campus-view-diagnostics.v1'
  viewId: CampusDestinationViewId
  camera: CampusCameraEvidence
  assetZone: AssetZoneSnapshot
  expectedZoneAssets: readonly string[]
  landmarks: readonly (CampusRenderEvidence & { file: string; plotId: string })[]
  destination: CampusRenderEvidence & CampusDestinationEvidenceSpec
}

/** Fixed exterior views for landmark readability and regression evidence. */
export const CAMPUS_ACCEPTANCE_VIEWS: Readonly<Record<CampusAcceptanceViewId, CampusAcceptancePose>> = {
  spawn: { x: 0, y: 1.65, z: 31, yawDeg: 0, pitchDeg: -4 },
  west: { x: -23, y: 1.65, z: 2.4, yawDeg: 0, pitchDeg: -6 },
  east: { x: 31, y: 1.65, z: 2.4, yawDeg: 0, pitchDeg: -6 },
  board: { x: -33.22, y: 1.65, z: 0.5, yawDeg: 0, pitchDeg: -4 },
  ops: { x: -15.44, y: 1.65, z: 0.5, yawDeg: 0, pitchDeg: -4 },
  hq: { x: 0, y: 1.65, z: 0.5, yawDeg: 0, pitchDeg: -4 },
  'exchange-entry': { x: 18.5, y: 1.65, z: 0.5, yawDeg: 0, pitchDeg: -4 },
  'exchange-silhouette': { x: 5.0, y: 1.65, z: 8.0, yawDeg: 30, pitchDeg: 20 },
  'workshop-entry': { x: 31.5, y: 1.65, z: 0.5, yawDeg: 0, pitchDeg: -4 },
  'workshop-silhouette': { x: 38.0, y: 1.65, z: 3.5, yawDeg: -28, pitchDeg: 8 },
  library: { x: 19.4, y: 1.65, z: 0.5, yawDeg: 180, pitchDeg: -4 },
  cafe: { x: -9.4, y: 1.65, z: 0.5, yawDeg: 180, pitchDeg: -4 },
  'datacentre-entry': { x: 24.3, y: 1.65, z: 0.5, yawDeg: 180, pitchDeg: -4 },
  'datacentre-silhouette': { x: 44.0, y: 1.65, z: -12.0, yawDeg: -154, pitchDeg: 22 },
  'north-east-context': { x: 8.0, y: 1.65, z: 7.0, yawDeg: 46, pitchDeg: 6 },
  'south-east-context': { x: 5.0, y: 1.65, z: 0.0, yawDeg: 135, pitchDeg: 30 },
}

export function campusAcceptanceView(id: CampusAcceptanceViewId): CampusAcceptancePose {
  return CAMPUS_ACCEPTANCE_VIEWS[id]
}

export interface CampusTraversalCheckpoint {
  plotId: string
  destination: string
  approachX: number
  x: number
  y: number
  z: number
  grounded: boolean
  doorwayReached: boolean
}

export interface CampusTraversalArtifact {
  schema: 'arena.campus-traversal.v1'
  status: 'measured'
  capturedAt: string
  colliderCount: number
  frames: number
  steppedUp: number
  steppedDown: number
  minY: number
  maxY: number
  checkpoints: CampusTraversalCheckpoint[]
  failures: string[]
  gate: {
    expectedDestinationCount: 8
    destinationCountPass: boolean
    groundedPass: boolean
    doorwayPass: boolean
    noFallPass: boolean
    passed: boolean
  }
  passed: boolean
}

interface WalkState {
  x: number
  y: number
  z: number
  grounded: boolean
}

interface WalkStats {
  frames: number
  steppedUp: number
  steppedDown: number
  minY: number
  maxY: number
  failures: string[]
}

const PLAYER_RADIUS = 0.3
const PLAYER_HEIGHT = 1.8
const WALK_STEP = 0.08
const TARGET_EPSILON = 0.04
const MAX_SEGMENT_FRAMES = 700
const STREET_ROUTE_Z = (CARRIAGEWAY_MIN_Z + CARRIAGEWAY_MAX_Z) / 2
const DOORWAY_DEPTH = 0.65

function walkCampusSegment(
  state: WalkState,
  target: { x: number; z: number },
  colliders: readonly AABB[],
  stats: WalkStats,
  label: string,
): boolean {
  let previousDistance = Infinity
  let stalledFrames = 0
  for (let frame = 0; frame < MAX_SEGMENT_FRAMES; frame += 1) {
    const dx = target.x - state.x
    const dz = target.z - state.z
    const distance = Math.hypot(dx, dz)
    if (distance <= TARGET_EPSILON) return true
    const step = Math.min(WALK_STEP, distance)
    const result = moveAndResolveCollisions(
      state,
      { x: dx / distance * step, y: -0.05, z: dz / distance * step },
      PLAYER_RADIUS,
      PLAYER_HEIGHT,
      colliders,
      { wasGrounded: state.grounded, maxStepUp: MAX_STEP, maxStepDown: MAX_STEP },
    )
    state.x = result.x
    state.y = result.y
    state.z = result.z
    state.grounded = result.grounded
    stats.frames += 1
    if (result.steppedUp) stats.steppedUp += 1
    if (result.steppedDown) stats.steppedDown += 1
    stats.minY = Math.min(stats.minY, state.y)
    stats.maxY = Math.max(stats.maxY, state.y)

    const nextDistance = Math.hypot(target.x - state.x, target.z - state.z)
    stalledFrames = nextDistance >= previousDistance - 1e-5 ? stalledFrames + 1 : 0
    previousDistance = nextDistance
    if (state.y < -0.5) {
      stats.failures.push(`${label}: fell below the campus walk surface at y=${state.y.toFixed(3)}`)
      return false
    }
    if (stalledFrames >= 20) {
      stats.failures.push(`${label}: collision-blocked ${nextDistance.toFixed(3)}m from the waypoint`)
      return false
    }
  }
  stats.failures.push(`${label}: exceeded ${MAX_SEGMENT_FRAMES} movement frames`)
  return false
}

/**
 * Walk one connected route from the district gate through every P1–P8 street
 * doorway using the production capsule resolver and the currently registered
 * scene colliders. Camera teleports cannot satisfy this gate.
 */
export function runCampusTraversalAcceptance(
  colliders: readonly AABB[],
  capturedAt = new Date().toISOString(),
): CampusTraversalArtifact {
  const state: WalkState = { x: 0, y: 0, z: 31, grounded: true }
  const stats: WalkStats = {
    frames: 0,
    steppedUp: 0,
    steppedDown: 0,
    minY: state.y,
    maxY: state.y,
    failures: [],
  }
  const checkpoints: CampusTraversalCheckpoint[] = []
  walkCampusSegment(state, { x: 0, z: STREET_ROUTE_Z }, colliders, stats, 'gate to High Street')

  const routeOrder = ['P1', 'P2', 'P3', 'P4', 'P5', 'P8', 'P7', 'P6']
  for (const plotId of routeOrder) {
    const plot = PLOTS.find((candidate) => candidate.id === plotId && candidate.enterable)
    if (!plot) {
      stats.failures.push(`${plotId}: enterable destination is missing`)
      continue
    }
    const [doorX, doorZ] = plotDoorPosition(plot)
    const outward = plot.side === 'north' ? 1 : -1
    // Stay inside the authored, furniture-free approach cone while stepping
    // around cars parked at the carriageway edge. The west leg is clear for
    // every authored entrance; P8's west-side industrial door in particular
    // would make an east dogleg intersect the south-east delivery van.
    const approachX = doorX - 2.2
    const outsideZ = doorZ + outward * DOORWAY_DEPTH
    const insideZ = doorZ - outward * DOORWAY_DEPTH
    const reachedRoad = walkCampusSegment(
      state,
      { x: approachX, z: STREET_ROUTE_Z },
      colliders,
      stats,
      `${plotId} street route`,
    )
    const reachedOutside = reachedRoad && walkCampusSegment(
      state,
      { x: approachX, z: outsideZ },
      colliders,
      stats,
      `${plotId} approach`,
    )
    const alignedWithDoor = reachedOutside && walkCampusSegment(
      state,
      { x: doorX, z: outsideZ },
      colliders,
      stats,
      `${plotId} threshold alignment`,
    )
    const doorwayReached = alignedWithDoor && walkCampusSegment(
      state,
      { x: doorX, z: insideZ },
      colliders,
      stats,
      `${plotId} doorway`,
    )
    checkpoints.push({
      plotId,
      destination: plot.name,
      approachX,
      ...state,
      doorwayReached,
    })
    if (!state.grounded) stats.failures.push(`${plotId}: player was not grounded at the doorway`)
    if (doorwayReached) {
      walkCampusSegment(
        state,
        { x: doorX, z: outsideZ },
        colliders,
        stats,
        `${plotId} exit doorway`,
      )
      walkCampusSegment(
        state,
        { x: approachX, z: outsideZ },
        colliders,
        stats,
        `${plotId} exit alignment`,
      )
      walkCampusSegment(
        state,
        { x: approachX, z: STREET_ROUTE_Z },
        colliders,
        stats,
        `${plotId} return to street`,
      )
    }
  }

  const destinationCountPass = checkpoints.length === 8
  const groundedPass = checkpoints.every((checkpoint) => checkpoint.grounded)
  const doorwayPass = checkpoints.every((checkpoint) => checkpoint.doorwayReached)
  const noFallPass = stats.minY >= -0.5
  const passed = destinationCountPass && groundedPass && doorwayPass && noFallPass && stats.failures.length === 0
  return {
    schema: 'arena.campus-traversal.v1',
    status: 'measured',
    capturedAt,
    colliderCount: colliders.length,
    frames: stats.frames,
    steppedUp: stats.steppedUp,
    steppedDown: stats.steppedDown,
    minY: stats.minY,
    maxY: stats.maxY,
    checkpoints,
    failures: stats.failures,
    gate: {
      expectedDestinationCount: 8,
      destinationCountPass,
      groundedPass,
      doorwayPass,
      noFallPass,
      passed,
    },
    passed,
  }
}
